"use client";
import { useState, useEffect, useMemo, Fragment } from "react";
import { BASEAPIURL, token } from "../env";
import GuestRoomSelector from "./GuestRoomSelector";
import "./roombooking.css";
import { useCart } from "./Founder/CartContext";
import { useNavigate } from "react-router-dom";
import { getDateRange } from "./commonFunctionlity";
import Backdrop from "@mui/material/Backdrop";
import logo from "../Images/logo-4.png";
import {
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const RoomBooking = () => {
  // Step Management
  const [step, setStep] = useState(1);
  const [adults, setAdults] = useState(2);
  const [rooms, setRooms] = useState(1);
  const [children, setChildren] = useState(0);
  const [extrabeds, setExtrabeds] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Member info
  const memberId = sessionStorage.getItem("MemberID") || "";
  const memberName = sessionStorage.getItem("MemberName") || "";
  const memberMobile = sessionStorage.getItem("MemMobileNo") || "";

  // Step 1: Booking Criteria
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const [checkInDate, setCheckInDate] = useState(
    today.toISOString().split("T")[0],
  );
  const [checkOutDate, setCheckOutDate] = useState(
    tomorrow.toISOString().split("T")[0],
  );
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);
  const [roomTypeCounts, setRoomTypeCounts] = useState({});
  const [locationId, setLocationId] = useState("");
  const [location, setLocation] = useState([]);

  // Availability
  const [isChecking, setIsChecking] = useState(false);
  const [availabilityResult, setAvailabilityResult] = useState([]);

  // Step 2: Guest List
  const [guests, setGuests] = useState([]);
  const [dependents, setDependents] = useState([]);

  // Pricing
  const [taxIncluded, setTaxIncluded] = useState(false);
  const [taxPercentage, setTaxPercentage] = useState(0);

  // Facility Details
  const [facilityDetails, setFacilityDetails] = useState(null);

  // Sundry Items
  const [sundryItems, setSundryItems] = useState([]);
  const [selectedSundryItems, setSelectedSundryItems] = useState([]);

  const [roomTypeList, setRoomTypeList] = useState([]);

  useEffect(() => {
    if (step === 2) {
      scrollToTop();
    }
  }, [step]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Status
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [bookingMessage, setBookingMessage] = useState("");

  const [isImgLoading, setIsImgLoading] = useState(true);
  const [imageByRoomTypeId, setImageByRoomTypeId] = useState([]);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [isGalleryLoading, setIsGalleryLoading] = useState(false);

  // Each room type will have its own independent guest/room count
  const [roomConfigs, setRoomConfigs] = useState({});

  // Max guests per room
  const MAX_GUESTS_PER_ROOM = 1;

  // Derived values
  const totalGuestsAllowed =
    Object.values(roomTypeCounts).reduce((sum, count) => sum + count, 0) *
    MAX_GUESTS_PER_ROOM;
  const guestCount = adults + children;

  const nights = useMemo(() => {
    if (!checkInDate || !checkOutDate) return 1;
    return Math.max(
      1,
      Math.ceil((new Date(checkOutDate) - new Date(checkInDate)) / 86400000),
    );
  }, [checkInDate, checkOutDate]);

  // ✅ Total Rooms
  const totalRooms = useMemo(() => {
    return Object.values(roomConfigs).reduce(
      (sum, r) => sum + (r.rooms || 0),
      0,
    );
  }, [roomConfigs]);

  // ✅ Detect Guest Booking
  const isGuestBooking = useMemo(() => {
    return guests.some((g) => g.bookingFor === "GUEST");
  }, [guests]);

  const calculateRoomPrices = useMemo(() => {
    let totalBase = 0;
    let totalTax = 0;
    let breakdown = [];

    Object.entries(roomConfigs).forEach(([roomId, config]) => {
      if (!config.rooms) return;

      const room = roomTypes.find((r) => String(r.RoomType_ID) === roomId);
      if (!room) return;

      const pricePerNight = isGuestBooking
        ? room?.GuestRoomPrice || room?.MemberRoomPrice
        : room?.MemberRoomPrice;

      const pricePerExtraBed = isGuestBooking
        ? room?.GuestExtraBedPrice || room?.MemberExtraBedPrice
        : room?.MemberExtraBedPrice;

      // Calculations
      const roomBaseCost = pricePerNight * config.rooms * nights;
      const totalExtraBedCost =
        (pricePerExtraBed || 0) * (config.extrabeds || 0) * nights;

      // Total amount for this specific room type before tax splitting
      const combinedBaseAmount = roomBaseCost + totalExtraBedCost;

      const isTaxIncluded =
        room?.TaxIsInclude === true || room?.TaxIsInclude === "Yes";
      const taxPercent = room?.TaxPercentage || 0;

      let roomTypeTax = 0;
      let roomTypeBase = 0;

      if (step === 2) {
        if (isTaxIncluded) {
          const baseWithoutTax = combinedBaseAmount / (1 + taxPercent / 100);
          roomTypeTax = combinedBaseAmount - baseWithoutTax;
          roomTypeBase = baseWithoutTax;
        } else {
          roomTypeTax = combinedBaseAmount * (taxPercent / 100);
          roomTypeBase = combinedBaseAmount;
        }
      } else {
        roomTypeTax = combinedBaseAmount * (taxPercent / 100);
        roomTypeBase = combinedBaseAmount;
      }

      // Update global accumulators for the whole booking
      totalBase += roomTypeBase;
      totalTax += roomTypeTax;

      // Push detailed data to breakdown
      breakdown.push({
        roomType: room.RoomType,
        rooms: config.rooms,
        pricePerNight,
        nights,
        guests: (config.adults || 0) + (config.children || 0),
        extraBeds: config.extrabeds || 0,
        extraBedPrice: totalExtraBedCost,
        base: roomTypeBase,
        tax: roomTypeTax,
        roomBaseCost,
        total: combinedBaseAmount,
      });
    });

    return {
      base: totalBase,
      tax: totalTax,
      total: totalBase + totalTax,
      breakdown,
    };
  }, [roomConfigs, roomTypes, nights, isGuestBooking, step]);

  const basePrice = calculateRoomPrices.base;
  const taxAmount = calculateRoomPrices.tax;
  const totalPrice = calculateRoomPrices.total;
  const breakdown = calculateRoomPrices.breakdown;

  // Auto-dismiss alerts
  useEffect(() => {
    if (bookingStatus || error) {
      const timer = setTimeout(() => {
        setBookingStatus(null);
        setError(null);
        setBookingMessage("");
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [bookingStatus, error]);

  const fetchRoomTypes = async () => {
    try {
      if (!checkInDate || !checkOutDate) {
        setError("Please select Check-in date and check-out Date");
        return;
      }
      const apiUrl = `${BASEAPIURL}/GetRoomTypePricelistNew?token=${encodeURIComponent(token)}&checkIn=${checkInDate}&checkOut=${checkOutDate}`;
      const res = await fetch(apiUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        setError("Failed to parse room types data.");
        return;
      }

      let roomTypeArray = [];
      if (Array.isArray(json)) {
        roomTypeArray = json;
      } else if (json?.data && Array.isArray(json.data)) {
        roomTypeArray = json.data;
      } else if (json?.RoomTypeData && Array.isArray(json.RoomTypeData)) {
        roomTypeArray = json.RoomTypeData;
      } else if (json && typeof json === "object") {
        roomTypeArray = [json];
      } else {
        roomTypeArray = [json];
      }

      const processedRoomTypes = roomTypeArray
        .filter((item) => item !== null && item !== undefined)
        .map((item) => ({
          RoomType_ID: item.RoomType_ID || item.roomtype_id || 0,
          RoomType: item.RoomType || item.roomtype || "Unknown",
          MemberRoomPrice: parseFloat(item.MemberRoomPrice) || 0,
          GuestRoomPrice: parseFloat(item.GuestRoomPrice) || 0,
          MemberExtraBedPrice: parseFloat(item.MemberExtraBedPrice) || 0,
          GuestExtraBedPrice: parseFloat(item.GuestExtraBedPrice) || 0,
          FacilityID: item.FacilityID || 0,
          TaxIsInclude: item.TaxIsInclude || "No",
          TaxPercentage: parseFloat(item.TaxPercentage) || 0,
          EC_RoomType_ID: roomTypes.EC_RoomType_ID || 0,
          AvailableRooms: item.AvailableRooms,
        }));

      setRoomTypes(processedRoomTypes.length ? processedRoomTypes : []);
      if (processedRoomTypes.length === 0) setError("No room types available.");
      // if (processedRoomTypes.length === 0){
      //   setError("No room types available.")
      // }else{
      //   setRoomTypeCounts()
      // }
    } catch (err) {
      setError("Failed to load room types.");
      setRoomTypes([]);
    }
  };

  const totalGuests = Object.values(roomConfigs)
    .filter((config) => config.rooms > 0) // ✅ only selected rooms
    .reduce((sum, config) => {
      return sum + (config.adults || 0) + (config.children || 0);
    }, 0);

  const totalExtraBeds = Object.values(roomConfigs)
    .filter((config) => config.rooms > 0) // ✅ only selected rooms
    .reduce((sum, config) => {
      return sum + (config.extrabeds || 0);
    }, 0);

  // Update facility & sundry when room changes
  useEffect(() => {
    if (selectedRoomTypes.length > 0) {
      const room = roomTypes.find(
        (r) => String(r.RoomType_ID) === selectedRoomTypes[0],
      );
      if (room) {
        setTaxIncluded(room.TaxIsInclude === true);
        setTaxPercentage(parseFloat(room.TaxPercentage) || 0);
        if (room.FacilityID) fetchFacilityDetails(room.FacilityID);
        if (room.RoomType_ID) fetchSundryItems(room.RoomType_ID);
      }
    }
  }, [selectedRoomTypes, roomTypes]);

  useEffect(() => {
    const activeRooms = Object.entries(roomConfigs)
      .filter(([_, config]) => config.rooms > 0)
      .map(([roomId]) => roomId);

    if (activeRooms.length > 0) {
      // 👉 Call API for first active room (or you can loop if needed)
      fetchFacilityDetails(activeRooms[0]);
      fetchSundryItems(activeRooms[0]);
    } else {
      setSundryItems([]);
    }
  }, [roomConfigs]);

  const fetchSundryItems = async (roomTypeId) => {
    try {
      const url = `${BASEAPIURL}/GetSundrybyRoomtypeID?token=${encodeURIComponent(token)}&RoomtypeID=${roomTypeId}`;
      const res = await fetch(url);
      const json = await res.json();

      if (Array.isArray(json)) {
        setSundryItems(json);
      } else if (json?.data && Array.isArray(json.data)) {
        setSundryItems(json.data);
      } else if (json?.RTSundry_ID) {
        setSundryItems([json]);
      } else {
        setSundryItems([]);
      }
    } catch (err) {
      setError("Failed to load sundry items.");
      setSundryItems([]);
    }
  };

  const fetchFacilityDetails = async (facilityId) => {
    try {
      const url = `${BASEAPIURL}/GetFacilityByID?FacilityID=${facilityId}&Token=${encodeURIComponent(token)}`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.status === "true") {
        setFacilityDetails({
          ...json.data,
          Facility_Image: Array.isArray(json.data.Facility_Image)
            ? json.data.Facility_Image
            : [],
        });
      }
    } catch (err) {
      console.error("Facility fetch failed:", err);
    }
  };
  const getImageByRoomTypeId = async (RoomTypeID) => {
    setIsGalleryLoading(true);
    try {
      const url = `${BASEAPIURL}/GetRoomTypeImageByID?RoomTypeID=${RoomTypeID}&token=${encodeURIComponent(token)}`;
      const res = await fetch(url);
      const json = await res.json();
      console.log({ json, res });

      if (json.status === true) {
        setImageByRoomTypeId(json.Data.Room_Image);
      }
    } catch (err) {
      console.error("Image fetch failed:", err);
    } finally {
      setIsGalleryLoading(false);
    }
  };

  const getRoomTypeList = async () => {
    setIsImgLoading(true);
    try {
      const res = await fetch(`${BASEAPIURL}/GetRoomTypeList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tokenData: token }),
      });
      const json = await res.json();
      if (Array.isArray(json.data)) {
        setRoomTypeList(json.data);
      }
    } catch (err) {
      setError("Failed to load room type list.");
    } finally {
      setIsImgLoading(false);
    }
  };

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await fetch(`${BASEAPIURL}/LocationListWithOutImage`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenData: token }),
        });
        const json = await res.json();
        if (Array.isArray(json.data)) {
          setLocation(json.data);
          if (!locationId && json.data.length > 0) {
            setLocationId(String(json.data[0].LocID));
          }
        }
      } catch (err) {
        setError("Failed to load locations.");
      }
    };
    fetchLocations();
  }, []);

  const handleRoomAvailabilByDate = async () => {
    if (!checkInDate || !checkOutDate) {
      setError("Please select Check-in date and check-out Date");
      return;
    }

    setIsChecking(true);
    setError("");

    try {
      const response = await fetch(
        `${BASEAPIURL}/GetRoomAvailability?Checkin=${checkInDate}&Checkout=${checkOutDate}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenData: token }),
        },
      );

      const result = await response.json();

      if (result.data) {
        // Assuming result.data contains the availability info
        setAvailabilityResult([result.data]);
        getRoomTypeList();
        fetchRoomTypes();
        // Logic for facility details
        const firstRoom = roomTypes.find(
          (r) => String(r.RoomType_ID) === selectedRoomTypes[0],
        );
        if (firstRoom?.FacilityID) {
          fetchFacilityDetails(firstRoom.FacilityID);
        }
      } else {
        setError(result.Message || "No rooms available.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to check availability.");
    } finally {
      setIsChecking(false);
    }
  };

  // Initialize guests when entering step 2
  useEffect(() => {
    if (step === 2 && guests.length === 0) {
      setGuests([
        {
          id: 1,
          bookingFor: "Self",
          name: memberName,
          contact: memberMobile,
          dependentId: "",
        },
      ]);
    }
  }, [step]);

  useEffect(() => {
    setRoomTypes([]);
  }, [checkInDate, checkOutDate]);

  // Fetch dependents
  useEffect(() => {
    if (step === 2 && memberId) {
      const fetchDeps = async () => {
        try {
          const res = await fetch(
            `${BASEAPIURL}/DependentListByMemID?MemID=${memberId}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ tokenData: token }),
            },
          );
          const json = await res.json();
          if (Array.isArray(json.data)) setDependents(json.data);
        } catch (err) {
          console.error("Fetch dependents failed:", err);
        }
      };
      fetchDeps();
    }
  }, [step, memberId]);

  // ✅ FIXED: Unified guest change handler
  const handleGuestChange = (id, field, value) => {
    setGuests((prev) =>
      prev.map((g) => {
        if (g.id !== id) return g;

        let updates = { [field]: value };

        if (field === "bookingFor") {
          if (value === "Self") {
            updates = {
              ...updates,
              name: memberName,
              contact: memberMobile,
              dependentId: "",
            };
          } else {
            // Clear fields when not Self
            updates = {
              ...updates,
              name: "",
              contact: "",
              dependentId: "",
            };
          }
        }

        return { ...g, ...updates };
      }),
    );
  };

  const handleDependentSelect = (id, depId) => {
    const dep = dependents.find((d) => String(d.DeptID) === depId);
    if (dep) {
      setGuests((prev) =>
        prev.map((g) =>
          g.id === id
            ? {
                ...g,
                dependentId: depId,
                name: dep.Name,
                contact: dep.Contact || dep.MobileNo || "",
              }
            : g,
        ),
      );
    }
  };

  const addGuest = () => {
    if (guests.length >= totalGuestsAllowed) {
      setError(`Maximum ${totalGuestsAllowed} guests allowed.`);
      return;
    }
    const newId = Math.max(...guests.map((g) => g.id), 0) + 1;
    setGuests([
      ...guests,
      {
        id: newId,
        bookingFor: "Guest",
        name: "",
        contact: "",
        dependentId: "",
      },
    ]);
  };

  const removeGuest = (id) => {
    if (guests.length <= 1) return;
    setGuests(guests.filter((g) => g.id !== id));
  };

  const handleSundrySelection = (sundryId) => {
    setSelectedSundryItems((prev) =>
      prev.includes(sundryId)
        ? prev.filter((id) => id !== sundryId)
        : [...prev, sundryId],
    );
  };

  const calculateSundryTotal = () => {
    return selectedSundryItems.reduce((total, sundryId) => {
      const sundry = sundryItems.find((item) => item.RTSundry_ID === sundryId);
      return total + (sundry ? sundry.RTSundry_Amount : 0);
    }, 0);
  };

  // ✅ IMPROVED: Guest validation with clear messages
  const validateGuests = () => {
    for (const g of guests) {
      if (g.bookingFor === "Self") continue;

      if (g.bookingFor === "DEPENDENT" && !g.dependentId) {
        return `Please select a dependent for guest #${g.id}`;
      }

      if (!g.name?.trim()) {
        return `Name is required for ${g.bookingFor.toLowerCase()} #${g.id}`;
      }

      if (!/^[6-9]\d{9}$/.test(g.contact)) {
        return `Valid 10-digit Indian mobile number required for ${g.name}`;
      }
    }
    return null;
  };

  const generateBookingDetails = () => {
    const bookingDetails = [];
    selectedRoomTypes.forEach((roomTypeId) => {
      const room = roomTypes.find((r) => String(r.RoomType_ID) === roomTypeId);
      const count = roomTypeCounts[roomTypeId] || 0;
      console.log(room)
      if (room && count > 0) {
        const price = parseFloat(room.MemberRoomPrice) || 0;
        const tax = taxIncluded
          ? price - price / (1 + taxPercentage / 100)
          : price * (taxPercentage / 100);
        bookingDetails.push({
          EventDate: checkInDate,
          BookingType: "Room",
          BookingRate: price,
          GSTAmount: tax,
          Is_TaxInclude: taxIncluded,
          TotalRoom: count,
          RoomTypeName: room.RoomType,
          // RoomTypeID: parseInt(selectedRoomTypes),
          // Deependra ------------------------
          RoomTypeID: room.RoomType_ID,
          // ---------------------------------------
          SundryDtl: selectedSundryItems.map((id) => {
            const sundry = sundryItems.find((item) => item.RTSundry_ID === id);
            return {
              SundryID: id,
              SundryName: sundry?.RTSundry_Type,
              SundryAmount: sundry?.RTSundry_Amount,
              EC_Sundry_ID: sundry?.RTSundry_EC_SundryID,
            };
          }),
        });
      }
    });
    return bookingDetails;
  };

  const handleSubmitBooking = async () => {
    try {
      const checkInTime = facilityDetails?.CheckInTime || "12:00:00";
      const checkOutTime = facilityDetails?.CheckOutTime || "11:00:00";
      const primaryGuest = guests[0];

      const firstRoom = roomTypes.find(
        (r) => String(r.RoomType_ID) === selectedRoomTypes[0],
      );

      const allSelectedRoomsData = selectedRoomTypes.map((id) => {
        return {
          id: id,
          config: roomConfigs[id],
          details: roomTypes.find((r) => String(r.RoomType_ID) === id),
        };
      });

      const selectedLocation = location.find(
        (l) => String(l.LocID) === locationId,
      );
      const totalRoomCount = Object.values(roomTypeCounts).reduce(
        (sum, count) => sum + count,
        0,
      );
      const sundaryTotal = calculateSundryTotal();

      const cartItem = {
        type: "Room",
        apiType: "room",
        details: {
          roomTypes: selectedRoomTypes.map((id) => ({
            type:
              roomTypes.find((r) => String(r.RoomType_ID) === id)?.RoomType ||
              "Unknown Room Type",
            count: roomTypeCounts[id] || 0,
          })),
          checkIn: checkInDate,
          checkOut: checkOutDate,
          rooms: totalRoomCount,
          guests: guestCount,
        },
        pricing: {
          roomRate: basePrice,
          base: basePrice,
          tax: taxAmount,
          sundary: sundaryTotal,
          total: totalPrice + sundaryTotal,
        },
        payload: {
          Member_ID: parseInt(memberId),
          RoomType_ID: parseInt(selectedRoomTypes),
          Member_Name: primaryGuest.name,
          Member_Contact: primaryGuest.contact,
          TotalRoom: totalRoomCount,
          CheckInDate: checkInDate,
          CheckOutDate: checkOutDate,
          // EC_RoomType_ID: firstRoom.EC_RoomType_ID,
          BookingDate: new Date().toISOString().slice(0, 19),
          BookingFor: primaryGuest.bookingFor,
          ExtraBed: extrabeds,
          Location: parseInt(locationId),
          GSTAmount: taxAmount,
          BookingAmount: totalPrice + sundaryTotal,
          BookingDtl: generateBookingDetails(),
          Sundry_IDs: selectedSundryItems.map((id) => {
            return {
              RTSundry_ID: id,
            };
          }),
        },
        RoomType:
          roomTypes.find((r) => String(r.RoomType_ID) === selectedRoomTypes[0])
            ?.RoomType || "",
      };

      addToCart(cartItem);
      const existingCart = JSON.parse(localStorage.getItem("cart") || "[]");
      localStorage.setItem("cart", JSON.stringify([...existingCart, cartItem]));
      navigate("/dashboard/cart");
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "N/A";

  const { addToCart } = useCart();
  const navigate = useNavigate();

  const { minDate, maxDate } = getDateRange(120);
  const { fromToMaxDate } = getDateRange(90, checkInDate);

  // Helper to update a specific property for a specific room ID
  const updateRoomConfig = (roomId, field, value) => {
    setRoomConfigs((prev) => {
      const room = roomTypes.find((r) => String(r.RoomType_ID) === roomId);
      const available = room?.AvailableRooms || 0;

      if (field === "rooms" && value > available) {
        setError(`Only ${available} Rooms available.`);
        return prev;
      }

      if (field === "rooms") {
        // Deependra
        setRoomTypeCounts((prev) => {
          const updated = { ...prev };
          if (value === 0) {
            delete updated[roomId]; // remove if 0
          } else {
            updated[roomId] = value; // set new count
          }
          return updated;
        });
        // ---------------------

        setSelectedRoomTypes((prevSelected) => {
          const idString = String(roomId);
          if (value > 0) {
            return prevSelected.includes(idString)
              ? prevSelected
              : [...prevSelected, idString];
          } else {
            return prevSelected.filter((id) => id !== idString);
          }
        });
      }

      return {
        ...prev,
        [roomId]: {
          ...(prev[roomId] || {
            rooms: 0,
            adults: 2,
            children: 0,
            extrabeds: 0,
          }),
          [field]: value,
        },
      };
    });
  };


  return (
    <div className="container-fluid w-100 py-4">
      <h3 className="text-center mb-4 fw-bold text-primary">🏨 Room Booking</h3>

      {/* Alerts */}
      {bookingStatus === "success" && (
        <div
          className="alert alert-success alert-dismissible fade show text-center"
          role="alert"
        >
          ✅ {bookingMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setBookingStatus(null)}
          ></button>
        </div>
      )}

      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          ⚠️ {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Step 1: Room Selection */}
      {step === 1 && (
        <div className="d-flex justify-content-center px-2">
          <div className="bg-white rounded-3 shadow p-md-4 w-100">
            <div className="row g-3 align-items-end d-flex flex-row">
              <div className="col-6 col-md-6 col-lg-5">
                <label className="form-label small">Check-in</label>
                <input
                  type="date"
                  className="form-control form-control-sm border border-secondary"
                  value={checkInDate}
                  onChange={(e) => setCheckInDate(e.target.value)}
                  onKeyDown={(e) => e.preventDefault()}
                  min={minDate}
                  max={maxDate}
                />
              </div>

              <div className="col-6 col-md-6 col-lg-5">
                <label className="form-label small">Check-out</label>
                <input
                  type="date"
                  className="form-control form-control-sm border border-secondary"
                  value={checkOutDate}
                  onChange={(e) => setCheckOutDate(e.target.value)}
                  onKeyDown={(e) => e.preventDefault()}
                  min={checkInDate || minDate}
                  max={fromToMaxDate}
                />
              </div>

              <div className="col-12 col-lg-2 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-warning rounded-pill w-100 btn-sm"
                  onClick={handleRoomAvailabilByDate}
                  disabled={isChecking}
                >
                  {isChecking ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Checking...
                    </>
                  ) : (
                    "SEARCH"
                  )}
                </button>
              </div>
            </div>

            {availabilityResult && (
              <div className="mt-3 mt-md-4">
                {/* <div className="container-fluid px-0"> */}

                {roomTypes.map((room, index) => {
                  const roomId = String(room.RoomType_ID);
                  const config = roomConfigs[roomId] || {
                    rooms: 0,
                    adults: 2,
                    children: 0,
                    extrabeds: 0,
                  };

                  const matchedRoomData = roomTypeList?.find(
                    (item) => String(item?.RoomType_ID) === roomId,
                  );

                  const roomImageUrl =
                    matchedRoomData?.Room_Image &&
                    matchedRoomData?.Room_Image.length > 0
                      ? matchedRoomData?.Room_Image[0]
                      : "https://images.unsplash.com/photo-1566073771259-6a8506099945";

                  return (
                    <div
                      key={roomId}
                      className="card shadow-sm mb-4 border-0"
                      style={{
                        zIndex: roomTypes.length - index,
                        position: "relative",
                        overflow: "visible",
                      }}
                    >
                      <div className="row g-0 align-items-center">
                        {/* LEFT SIDE: Image Section */}
                        <div className="col-12 col-lg-5">
                          <div
                            className="position-relative overflow-hidden rounded-start"
                            style={{
                              height: "400px",
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            {/* 2. Loader: Visible only when isImgLoading is true */}
                            {isImgLoading && (
                              <div className="position-absolute top-50 start-50 translate-middle d-flex flex-column align-items-center w-100">
                                <div
                                  className="spinner-border spinner-border-sm text-primary"
                                  role="status"
                                ></div>
                                <small className="text-muted mt-2">
                                  Loading Image...
                                </small>
                              </div>
                            )}

                            <img
                              src={roomImageUrl}
                              alt={room.RoomType}
                              className={`img-fluid w-100 h-100 ${isImgLoading ? "d-none" : "d-block"}`}
                              style={{
                                objectFit: "cover",
                                height: "400px",
                                cursor: "pointer",
                              }}
                              onClick={async () => {
                                setCurrentImgIndex(0);
                                await getImageByRoomTypeId(roomId);
                              }}
                              onError={(e) => {
                                setIsImgLoading(false);
                                e.target.src =
                                  "https://images.unsplash.com/photo-1566073771259-6a8506099945";
                              }}
                            />

                            {/* Room Type Badge */}
                            <div className="position-absolute top-0 start-0 m-2">
                              <span
                                className="badge bg-dark px-2 py-1 shadow-sm text-uppercase"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {room.RoomType}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* RIGHT SIDE: Room Details */}
                        <div className="col-12 col-lg-7 d-flex flex-column bg-white">
                          <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h3 className="fw-bold mb-1 text-uppercase text-primary">
                                  {room.RoomType}
                                </h3>
                              </div>
                              <div className="text-end">
                                <span className="h4 fw-bold text-dark d-block mb-0">
                                  ₹
                                  {(guests.some((g) => g.bookingFor === "GUEST")
                                    ? room.GuestRoomPrice ||
                                      room.MemberRoomPrice
                                    : room.MemberRoomPrice
                                  ).toFixed(2)}
                                </span>
                              </div>
                            </div>

                            {/* INTEGRATED GUEST SELECTOR */}
                            <div
                              className={`position-relative p-3 rounded border mb-3 transition-all ${config.rooms > 0 ? "bg-white border-primary shadow-sm" : "bg-light"}`}
                            >
                              <GuestRoomSelector
                                rooms={config.rooms}
                                adults={config.adults}
                                children={config.children}
                                extrabeds={config.extrabeds}
                                setRooms={(val) =>
                                  updateRoomConfig(roomId, "rooms", val)
                                }
                                setAdults={(val) =>
                                  updateRoomConfig(roomId, "adults", val)
                                }
                                setChildren={(val) =>
                                  updateRoomConfig(roomId, "children", val)
                                }
                                setExtrabeds={(val) =>
                                  updateRoomConfig(roomId, "extrabeds", val)
                                }
                              />
                            </div>

                            {/* Action Footer for individual Card */}
                            <div className="mt-auto pt-3 border-top d-flex justify-content-between align-items-center">
                              <div>
                                {config.rooms > 0 && (
                                  <span className="text-success fw-bold small">
                                    <i className="bi bi-check-circle-fill me-1"></i>
                                    {config.rooms}{" "}
                                    {config.rooms === 1 ? "Room" : "Rooms"}{" "}
                                    Added
                                  </span>
                                )}
                              </div>

                              {config.rooms === 0 ? (
                                <button
                                  className="btn btn-primary px-4 fw-bold"
                                  onClick={() =>
                                    updateRoomConfig(roomId, "rooms", 1)
                                  }
                                >
                                  Add This Room
                                </button>
                              ) : (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() =>
                                    updateRoomConfig(roomId, "rooms", 0)
                                  }
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {roomTypes.length > 0 && totalRooms > 0 && (
                  <div className="fixed-bottom bg-white border-top shadow-lg">
                    <div className="container">
                      {/* Arrow Button */}
                      <div className="d-flex justify-content-end">
                        <button
                          className="btn btn-light border rounded-circle p-2"
                          onClick={() => setIsOpen(!isOpen)}
                        >
                          {isOpen ? <FaChevronDown /> : <FaChevronUp />}
                        </button>
                      </div>

                      {/* Collapse Content */}
                      <div className={`mt-2 ${isOpen ? "d-block" : "d-none"}`}>
                        {breakdown.map((item, index) => (
                          <div key={index} className="mb-3  pb-2">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <div className="small">
                                <div className="fw-semibold">
                                  {item.roomType}
                                </div>
                                <div className="text-muted">
                                  {item.rooms} Room × {item.nights} Night(s)
                                  {" • "} 👤 {item.guests} Guest
                                  {item.guests > 1 ? "s" : ""}
                                </div>
                              </div>
                              <div className="fw-bold text-dark">
                                ₹{item.roomBaseCost?.toFixed(2)}
                              </div>
                            </div>

                            {/* This will now work because item.extraBeds is defined */}
                            {item.extraBeds > 0 && (
                              <div className="d-flex justify-content-between align-items-center mb-1 ps-3">
                                <div className="small text-muted">
                                  <span>
                                    Extra Bed ({item.extraBeds} × {item.nights}{" "}
                                    nights)
                                  </span>
                                </div>
                                <div className="small fw-semibold text-dark">
                                  ₹{item.extraBedPrice?.toFixed(2)}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                        <hr className="my-2" />
                      </div>
                    </div>

                    {/* 🔼 Bottom Summary Bar */}
                    <div className="container d-flex justify-content-between align-items-center py-3">
                      <div>
                        <h5 className="mb-0 fw-bold text-primary">
                          ₹{basePrice.toFixed(2)}
                        </h5>
                        <small className="text-muted">
                          {totalRooms} {totalRooms === 1 ? "Room" : "Rooms"}
                        </small>
                      </div>

                      <button
                        className="btn btn-success px-4 fw-bold shadow-sm"
                        onClick={() => setStep(2)}
                      >
                        Proceed to Booking →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {isGalleryLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
          style={{
            zIndex: 10001,
            backgroundColor: "rgba(201, 190, 190, 0.4)",
            // backdropFilter: "blur(5px)",
          }}
        >
          <Backdrop
            sx={{
              color: "#070707",
              bgcolor: "rgb(216, 227, 240,0.4)",
              zIndex: 9999,
            }}
            open={true}
          >
            <div className="loader">
              <img src={logo} alt="logo of website" />
            </div>
          </Backdrop>
        </div>
      )}

      {/* ✅ Lightbox Modal for Base64 Images */}
      {imageByRoomTypeId && imageByRoomTypeId.length > 0 && (
        <div
          className="modal fade show d-block"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.95)",
            zIndex: 10000,
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content bg-transparent border-0">
              {/* Close Button: Isse state null karein taaki modal band ho jaye */}
              <button
                className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
                onClick={() => setImageByRoomTypeId([])}
                style={{ zIndex: 11, filter: "invert(1)" }}
              ></button>

              <div className="modal-body p-0 position-relative text-center">
                <div
                  className="position-absolute top-50 start-50 translate-middle"
                  style={{ zIndex: -1 }}
                >
                  <div className="spinner-border text-primary"></div>
                </div>

                <img
                  key={currentImgIndex} // Key change karne se loader har image change par trigger hoga agar image slow hai
                  src={imageByRoomTypeId[currentImgIndex]}
                  className="img-fluid rounded shadow-lg"
                  style={{
                    maxHeight: "70vh",
                    objectFit: "contain",
                    backgroundColor: "#111",
                  }}
                  alt="Room Gallery"
                />

                {/* 🔢 Image Counter */}
                <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3">
                  <span className="badge bg-dark px-3 py-2 border border-secondary">
                    {currentImgIndex + 1} / {imageByRoomTypeId.length}
                  </span>
                </div>

                {/* ⬅️ Previous & ➡️ Next Buttons */}
                {imageByRoomTypeId.length > 1 && (
                  <>
                    <button
                      className="btn btn-dark position-absolute top-50 start-0 translate-middle-y ms-2 rounded-circle border-secondary shadow"
                      style={{
                        width: "45px",
                        height: "45px",
                      }}
                      onClick={() =>
                        setCurrentImgIndex((prev) =>
                          prev === 0 ? imageByRoomTypeId.length - 1 : prev - 1,
                        )
                      }
                    >
                      <FaChevronLeft />
                    </button>
                    <button
                      className="btn btn-dark position-absolute top-50 end-0 translate-middle-y me-2 rounded-circle border-secondary shadow"
                      style={{
                        width: "45px",
                        height: "45px",
                      }}
                      onClick={() =>
                        setCurrentImgIndex((prev) =>
                          prev === imageByRoomTypeId.length - 1 ? 0 : prev + 1,
                        )
                      }
                    >
                      <FaChevronRight />
                    </button>
                  </>
                )}
              </div>

              {/* 🎞️ Scrollable Thumbnails List */}
              <div className="mt-4 px-2">
                <div
                  className="d-flex justify-content-center overflow-auto py-2 custom-scrollbar"
                  style={{ gap: "12px" }}
                >
                  {imageByRoomTypeId.map((imgBase64, idx) => (
                    <img
                      key={idx}
                      src={imgBase64} // Direct Base64 String
                      onClick={() => setCurrentImgIndex(idx)}
                      className={`rounded border-2 shadow-sm ${currentImgIndex === idx ? "border-primary" : "border-white opacity-50"}`}
                      style={{
                        width: "90px",
                        height: "65px",
                        objectFit: "cover",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Guest & Booking Details */}
      {step === 2 && (
        <div className="row g-5">
          <div className="col-12 col-lg-7">
            {/* Guest List */}
            {guests.map((g) => (
              <div
                key={g.id}
                className="border rounded p-3 mb-3 bg-light border-2 border-primary"
              >
                <div className="d-flex justify-content-between flex-wrap">
                  {guests.length > 1 && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeGuest(g.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="mb-2">
                  <label>Booking For</label>
                  <select
                    className="form-select"
                    value={g.bookingFor}
                    onChange={(e) =>
                      handleGuestChange(g.id, "bookingFor", e.target.value)
                    }
                  >
                    <option value="Self">MEMBER ({memberName})</option>
                    <option value="DEPENDENT">DEPENDENT</option>
                    <option value="GUEST">GUEST</option>
                  </select>
                </div>

                {g.bookingFor === "DEPENDENT" && (
                  <div className="mb-2">
                    <label>Select Dependent</label>
                    <select
                      className="form-select"
                      value={g.dependentId}
                      onChange={(e) =>
                        handleDependentSelect(g.id, e.target.value)
                      }
                    >
                      <option value="">-- Choose --</option>
                      {dependents.map((d) => (
                        <option key={d.DeptID} value={d.DeptID}>
                          {d.Name} ({d.Relation})
                        </option>
                      ))}
                    </select>
                    {!g.dependentId && (
                      <div className="text-danger small mt-1">
                        Please select a dependent.
                      </div>
                    )}
                  </div>
                )}

                {(g.bookingFor === "GUEST" ||
                  (g.bookingFor === "DEPENDENT" && g.dependentId)) && (
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label>Full Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={g.name}
                        onChange={(e) =>
                          handleGuestChange(g.id, "name", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label>Contact (10-digit Indian)</label>
                      <input
                        type="tel"
                        className={`form-control ${g.contact && !/^[6-9]\d{9}$/.test(g.contact) ? "is-invalid" : ""}`}
                        value={g.contact}
                        onChange={(e) =>
                          handleGuestChange(g.id, "contact", e.target.value)
                        }
                      />
                      {g.contact && !/^[6-9]\d{9}$/.test(g.contact) && (
                        <div className="invalid-feedback d-block">
                          Enter valid mobile number
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <h5>
              <i className="bi bi-people"></i> Booking Details ({memberName})
            </h5>

            <div className="mb-4">
              {Object.entries(roomConfigs)
                .filter(([_, config]) => config.rooms > 0)
                .map(([roomTypeId, config]) => {
                  const room = roomTypes.find(
                    (r) => String(r.RoomType_ID) === roomTypeId,
                  );

                  const pricePerNight = isGuestBooking
                    ? room?.GuestRoomPrice || room?.MemberRoomPrice
                    : room?.MemberRoomPrice;

                  return (
                    <div
                      key={roomTypeId}
                      className="border rounded p-3 mb-3 bg-light shadow-sm"
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        {/* Room Name */}
                        <div>
                          <div className="fw-semibold">
                            {room?.RoomType || "Unknown Room"} ({pricePerNight})
                          </div>
                          <small className="text-muted">
                            {config.adults} Adult • {config.children} Child
                          </small>
                        </div>

                        <div className="d-flex align-items-center">
                          <button
                            className="btn btn-sm btn-outline-secondary me-2"
                            onClick={() => {
                              if (config.rooms > 1) {
                                updateRoomConfig(
                                  roomTypeId,
                                  "rooms",
                                  Math.max(1, config.rooms - 1),
                                );
                              }
                            }}
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            className="form-control w-50 text-center"
                            value={config.rooms}
                            onChange={(e) => {
                              const val = Math.max(
                                0,
                                parseInt(e.target.value) || 0,
                              );
                              updateRoomConfig(roomTypeId, "rooms", val);
                            }}
                          />

                          {/* Plus */}
                          <button
                            className="btn btn-sm btn-outline-secondary ms-2"
                            onClick={() =>
                              updateRoomConfig(
                                roomTypeId,
                                "rooms",
                                Math.min(4, config.rooms + 1),
                              )
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Sundry Items */}
            {sundryItems.length > 0 && (
              <div className="border rounded p-3 mb-4 bg-light">
                <h5 className="mb-3">Services</h5>
                <div className="row g-3">
                  {sundryItems.map((item) => (
                    <div key={item.RTSundry_ID} className="col-12">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`sundry-${item.RTSundry_ID}`}
                          checked={selectedSundryItems.includes(
                            item.RTSundry_ID,
                          )}
                          onChange={() =>
                            handleSundrySelection(item.RTSundry_ID)
                          }
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor={`sundry-${item.RTSundry_ID}`}
                        >
                          <span className="fw-semibold">
                            {item.RTSundry_Name}
                          </span>
                          <span className="text-muted ms-2">
                            ₹{item.RTSundry_Amount.toFixed(2)}
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex flex-column flex-sm-row gap-2 mt-4">
              <button
                className="btn btn-outline-secondary w-50"
                onClick={() => setStep(1)}
              >
                ← Back
              </button>
              <button
                className="btn btn-success w-50"
                onClick={handleSubmitBooking}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing..." : "Add to Cart"}
              </button>
            </div>
          </div>

          {/* Booking Summary */}
          <div className="col-12 col-lg-5">
            <div
              className="card shadow-sm sticky-top border-0 rounded-3"
              style={{ top: 20 }}
            >
              <div className="card-body p-4">
                <h5 className="mb-3 fw-bold border-bottom pb-2">
                  Room Information
                </h5>
                {Object.entries(roomConfigs)
                  .filter(([_, config]) => config.rooms > 0)
                  .map(([roomTypeId, config]) => {
                    const room = roomTypes.find(
                      (r) => String(r.RoomType_ID) === roomTypeId,
                    );

                    const pricePerNight = isGuestBooking
                      ? room?.GuestRoomPrice || room?.MemberRoomPrice
                      : room?.MemberRoomPrice;

                    const pricePerExtraBad = isGuestBooking
                      ? room?.GuestExtraBedPrice || room?.MemberExtraBedPrice
                      : room?.MemberExtraBedPrice;

                    // Calculations
                    const roomBaseCost = pricePerNight * config.rooms * nights;
                    const totalExtraBedCost =
                      (pricePerExtraBad || 0) *
                      (config.extrabeds || 0) *
                      nights;

                    // Total amount before tax splitting
                    const combinedBaseAmount = roomBaseCost + totalExtraBedCost;

                    const isTaxIncluded =
                      room?.TaxIsInclude === true ||
                      room?.TaxIsInclude === "Yes";
                    const taxPercent = room?.TaxPercentage || 0;

                    let totalCalculatedTax = 0;
                    let totalBasePrice = 0;

                    if (isTaxIncluded) {
                      const baseWithoutTax =
                        combinedBaseAmount / (1 + taxPercent / 100);
                      totalCalculatedTax = combinedBaseAmount - baseWithoutTax;
                      totalBasePrice =
                        combinedBaseAmount.toFixed(2) -
                        totalCalculatedTax.toFixed(2);
                    } else {
                      totalCalculatedTax =
                        combinedBaseAmount * (taxPercent / 100);
                      totalBasePrice = combinedBaseAmount;
                    }

                    const cgst = totalCalculatedTax / 2;
                    const sgst = totalCalculatedTax / 2;

                    const totalGuestsInRoom =
                      (config.adults || 0) + (config.children || 0);

                    return (
                      <Fragment key={roomTypeId}>
                        <div className="mb-3 border-bottom pb-3">
                          {/* Room Type */}
                          <div className="d-flex justify-content-between mb-1">
                            <span>Room Type</span>
                            <span className="fw-semibold">
                              {room?.RoomType || "Unknown"}{" "}
                            </span>
                          </div>

                          {/* Price */}
                          <div className="d-flex justify-content-between mb-1">
                            <span>Room Price ({config.rooms} Rooms)</span>
                            <span className="fw-semibold text-success">
                              ₹{roomBaseCost.toFixed(2)}
                            </span>
                          </div>
                          {/* Guests */}
                          <div className="d-flex justify-content-between mb-1">
                            <span>Guests</span>
                            <span className="fw-semibold">
                              {totalGuestsInRoom}
                            </span>
                          </div>

                          {/* ✅ Extra Bed per room */}
                          {config.extrabeds > 0 && (
                            <div className="d-flex justify-content-between mb-1">
                              <span>
                                Extra Bed ({config.extrabeds} × {nights} nights)
                              </span>
                              <span className="fw-semibold">
                                ₹{totalExtraBedCost.toFixed(2)}
                              </span>
                            </div>
                          )}

                          {/* ✅ CGST */}
                          <div className="d-flex justify-content-between small text-muted">
                            <span>
                              CGST ({taxPercent * 0.5}%){" "}
                              {isTaxIncluded ? "TaxIncluded" : "TaxExcluded"}
                            </span>
                            <span>₹{cgst.toFixed(2)}</span>
                          </div>

                          {/* ✅ SGST */}
                          <div className="d-flex justify-content-between small text-muted">
                            <span>SGST ({taxPercent / 2}%)</span>
                            <span>₹{sgst.toFixed(2)}</span>
                          </div>
                        </div>
                      </Fragment>
                    );
                  })}

                {selectedSundryItems.length > 0 && (
                  <div className="mb-3">
                    <h5 className="mb-2">Services</h5>
                    {selectedSundryItems.map((sundryId) => {
                      const sundry = sundryItems.find(
                        (item) => item.RTSundry_ID === sundryId,
                      );
                      return sundry ? (
                        <div
                          key={sundryId}
                          className="d-flex justify-content-between mb-1"
                        >
                          <span>{sundry.RTSundry_Name}</span>
                          <span>₹{sundry.RTSundry_Amount.toFixed(2)}</span>
                        </div>
                      ) : null;
                    })}
                    <div className="d-flex justify-content-between fw-bold mt-2 pt-2 border-top">
                      <span>Total Sundry</span>
                      <span>₹{calculateSundryTotal().toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <h5 className="mb-4 fw-bold border-bottom pb-2">
                  Booking Summary
                </h5>
                <div className="d-flex justify-content-between mb-2">
                  <span>Check-in</span>
                  <span className="fw-semibold">{formatDate(checkInDate)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Check-out</span>
                  <span className="fw-semibold">
                    {formatDate(checkOutDate)}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Base Amount</span>
                  <span className="fw-semibold">₹{basePrice.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Total Tax Amount</span>
                  <span className="fw-semibold">₹{taxAmount.toFixed(2)}</span>
                </div>

                {selectedSundryItems.length > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <span>Sundry Items</span>
                    <span className="fw-semibold">
                      ₹{calculateSundryTotal().toFixed(2)}
                    </span>
                  </div>
                )}
                <hr />
                <div className="d-flex justify-content-between align-items-center fs-5 fw-bold text-primary">
                  <span>Total</span>
                  <span>
                    ₹{(totalPrice + calculateSundryTotal()).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomBooking;
